/**
 * StyleManager - Manages CSS styles and responsive breakpoints
 *
 * The admin panel is the single source of truth for which viewport is active.
 * Chrome scales iframe viewports based on parent CSS transforms, so
 * window.innerWidth inside the iframe does NOT reflect the intended device
 * width.  Instead, the admin panel sends the viewport name ('desktop',
 * 'tablet', 'mobile') via:
 *   - 'viewport-change' messages (when the user switches viewport)
 *   - 'full-update' messages (includes viewport on every sync)
 *
 * The runtime stores this viewport and uses it to pick the correct
 * breakpoint value for each responsive style, applied as an inline style.
 *
 * CSS media queries are also injected (without !important) for the
 * deployed site where no admin panel / runtime messaging exists.
 *
 * Breakpoints for deployed CSS:
 *   Mobile:  base rule (no media query)
 *   Tablet:  @media (min-width: 768px) and (max-width: 1199px)
 *   Desktop: @media (min-width: 1200px)
 *
 * Only applies styles from an allowed whitelist.
 * Generates selectors scoped to data-dr-section containers.
 */

const ALLOWED_STYLE_PROPS = [
  // Text
  'textAlign', 'fontWeight', 'fontSize', 'letterSpacing',
  'lineHeight', 'color', 'textTransform', 'opacity',
  // Spacing
  'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
  'marginTop', 'marginBottom',
  // Layout
  'gap', 'justifyContent', 'alignItems', 'flexDirection',
  // Background
  'backgroundColor', 'borderRadius',
];

interface ResponsiveEntry {
  sectionId: string;
  field: string;
  property: string;
  value: { mobile?: string; tablet?: string; desktop?: string };
}

export class StyleManager {
  private styleSheets: Map<string, HTMLStyleElement> = new Map();
  /** Stores every responsive value for re-application on viewport change */
  private responsiveEntries: Map<string, ResponsiveEntry> = new Map();
  /** Current viewport name — set by admin panel, NOT by window.innerWidth */
  private currentViewport: 'desktop' | 'tablet' | 'mobile' = 'desktop';

  /**
   * Set the current viewport.  Called when the runtime receives
   * 'viewport-change' or 'full-update' from the admin panel.
   */
  setViewport(viewport: 'desktop' | 'tablet' | 'mobile'): void {
    console.log(`[style-manager] setViewport: ${this.currentViewport} → ${viewport}`);
    this.currentViewport = viewport;
  }

  /**
   * Handle a style-update message from the admin panel.
   * Each property value can be either a flat string or a responsive object
   * { mobile, tablet, desktop }.
   */
  handleStyleUpdate(sectionId: string, field: string, styles: Record<string, any>): void {
    const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
    if (!sectionEl) return;

    const el = field === '__section'
      ? sectionEl as HTMLElement
      : sectionEl.querySelector(`[data-dr-style="${field}"]`) as HTMLElement | null;
    if (!el) return;

    for (const [prop, value] of Object.entries(styles)) {
      if (!ALLOWED_STYLE_PROPS.includes(prop)) continue;

      const isResponsive = typeof value === 'object' && value !== null && !Array.isArray(value)
        && ('mobile' in value || 'tablet' in value || 'desktop' in value);
      if (isResponsive) {
        this.applyResponsiveStyle(sectionId, field, prop, value);
      } else {
        (el.style as any)[prop] = value;
      }
    }
  }

  /**
   * Apply a responsive style for a single property.
   *
   * Uses the stored currentViewport (set by admin panel messages) to pick
   * the correct breakpoint value and apply it as an inline style.
   * Also injects CSS media-query rules (without !important) for the
   * deployed site.
   */
  private applyResponsiveStyle(
    sectionId: string,
    field: string,
    property: string,
    value: { mobile?: string; tablet?: string; desktop?: string }
  ): void {
    const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
    if (!sectionEl) return;

    const el = field === '__section'
      ? sectionEl as HTMLElement
      : sectionEl.querySelector(`[data-dr-style="${field}"]`) as HTMLElement | null;
    if (!el) return;

    // Store entry so applyViewport() can re-apply later
    const key = `${sectionId}|${field}|${property}`;
    this.responsiveEntries.set(key, { sectionId, field, property, value });

    // Apply the value for the CURRENT viewport (set by admin panel) as inline style
    const activeValue = this.pickValueForViewport(value, this.currentViewport);
    console.log(`[style-manager] applyResponsive`, {
      viewport: this.currentViewport,
      property,
      value,
      picked: activeValue,
      windowInnerWidth: window.innerWidth,
    });
    if (activeValue) {
      (el.style as any)[property] = activeValue;
    }

    // --- CSS media-query rules (no !important — inline style takes priority in preview,
    //     these rules are for the deployed site where no runtime messaging exists) ---
    const styleId = `responsive-${sectionId}-${field}-${property}`;
    const selector = field === '__section'
      ? `[data-dr-section="${sectionId}"]`
      : `[data-dr-section="${sectionId}"] [data-dr-style="${field}"]`;
    const prop = this.camelToKebab(property);

    const parts: string[] = [];

    if (value.mobile) {
      parts.push(`${selector} { ${prop}: ${value.mobile}; }`);
    }

    if (value.tablet) {
      parts.push(`@media (min-width: 768px) and (max-width: 1199px) { ${selector} { ${prop}: ${value.tablet}; } }`);
    }

    if (value.desktop) {
      parts.push(`@media (min-width: 1200px) { ${selector} { ${prop}: ${value.desktop}; } }`);
    }

    if (parts.length > 0) {
      this.injectStyleSheet(styleId, parts.join('\n'));
    } else {
      this.removeStyleSheet(styleId);
    }
  }

  /**
   * Pick the value for a named viewport.
   * Falls back: desktop → tablet → mobile, tablet → mobile.
   */
  private pickValueForViewport(
    value: { mobile?: string; tablet?: string; desktop?: string },
    viewport: 'desktop' | 'tablet' | 'mobile'
  ): string {
    if (viewport === 'desktop') return value.desktop || value.tablet || value.mobile || '';
    if (viewport === 'tablet') return value.tablet || value.mobile || '';
    return value.mobile || '';
  }

  /**
   * Re-apply all stored responsive styles for the given viewport name.
   * Called when the runtime receives 'viewport-change' from the admin panel.
   */
  applyViewport(viewport: 'desktop' | 'tablet' | 'mobile'): void {
    console.log(`[style-manager] applyViewport: ${viewport} (${this.responsiveEntries.size} entries)`);
    this.currentViewport = viewport;

    for (const { sectionId, field, property, value } of this.responsiveEntries.values()) {
      const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
      if (!sectionEl) continue;

      const el = field === '__section'
        ? sectionEl as HTMLElement
        : sectionEl.querySelector(`[data-dr-style="${field}"]`) as HTMLElement | null;
      if (!el) continue;

      const newValue = this.pickValueForViewport(value, viewport);
      if (newValue) {
        (el.style as any)[property] = newValue;
      }
    }
  }

  /**
   * Inject CSS into document as a style element.
   */
  private injectStyleSheet(id: string, css: string): void {
    this.removeStyleSheet(id);

    const styleElement = document.createElement('style');
    styleElement.id = id;
    styleElement.textContent = css;
    document.head.appendChild(styleElement);
    this.styleSheets.set(id, styleElement);
  }

  /**
   * Remove a style element by ID.
   */
  private removeStyleSheet(id: string): void {
    const existing = this.styleSheets.get(id);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
      this.styleSheets.delete(id);
    }
    const byId = document.getElementById(id) as HTMLStyleElement | null;
    if (byId && byId.parentNode) {
      byId.parentNode.removeChild(byId);
    }
  }

  private camelToKebab(str: string): string {
    return str.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
  }

  getActiveStyleSheets(): string[] {
    return Array.from(this.styleSheets.keys());
  }

  clearAllStyleSheets(): void {
    for (const id of this.styleSheets.keys()) {
      this.removeStyleSheet(id);
    }
  }

  destroy(): void {
    this.clearAllStyleSheets();
    this.responsiveEntries.clear();
  }
}
